import React, { useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { IconButton } from "../IconButton/IconButton";
import { mergeClassNames } from "../../design-tokens";
import styles from "./Modal.module.css";

/**
 * Tamanhos disponíveis para o Modal
 * - sm: 28rem (modal compacto — formulários simples)
 * - md: 32rem (padrão — formulários médios)
 * - lg: 42rem (modal largo — formulários complexos)
 */
export type ModalSize = "sm" | "md" | "lg";

/**
 * Props do componente Modal
 */
export interface ModalProps {
  /** Controla a visibilidade do modal */
  visible: boolean;
  /** Callback chamado ao fechar o modal */
  onHide: () => void;
  /** Título exibido no header do modal */
  header?: string | React.ReactNode;
  /** Conteúdo do footer (geralmente botões de ação) */
  footer?: React.ReactNode;
  /** Conteúdo do corpo do modal */
  children: React.ReactNode;
  /** Tamanho do modal @default "md" */
  size?: ModalSize;
  /** Exibe o botão de fechar no header @default true */
  closable?: boolean;
  /** Permite fechar com a tecla Escape @default true */
  closeOnEscape?: boolean;
  /** Permite fechar ao clicar no overlay @default true */
  closeOnOverlayClick?: boolean;
  /** Classes CSS adicionais para o container */
  className?: string;
}

/**
 * Modal Component
 *
 * Componente modal genérico do Design System.
 * Substitui o uso direto do `Dialog` do PrimeReact.
 *
 * @example
 * // Modal simples com header e footer
 * <Modal
 *   visible={showModal}
 *   onHide={() => setShowModal(false)}
 *   header="Adicionar Item"
 *   footer={<Button label="Salvar" onClick={handleSave} />}
 * >
 *   <Input label="Nome" value={name} onChange={setName} />
 * </Modal>
 */
export const Modal: React.FC<ModalProps> = ({
  visible,
  onHide,
  header,
  footer,
  children,
  size = "md",
  closable = true,
  closeOnEscape = true,
  closeOnOverlayClick = true,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<Element | null>(null);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (visible) {
      previousActiveElement.current = document.activeElement;
      document.body.style.overflow = "hidden";
      // Focus the modal container for keyboard navigation
      requestAnimationFrame(() => {
        containerRef.current?.focus();
      });
    } else {
      document.body.style.overflow = "";
      // Restore focus to the element that was focused before opening the modal
      if (previousActiveElement.current instanceof HTMLElement) {
        previousActiveElement.current.focus();
      }
    }
    return (): void => {
      document.body.style.overflow = "";
    };
  }, [visible]);

  // Handle Escape key
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent): void => {
      if (closeOnEscape && e.key === "Escape") {
        e.stopPropagation();
        onHide();
      }
    },
    [closeOnEscape, onHide],
  );

  // Handle overlay click
  const handleOverlayClick = useCallback(
    (e: React.MouseEvent): void => {
      if (closeOnOverlayClick && e.target === e.currentTarget) {
        onHide();
      }
    },
    [closeOnOverlayClick, onHide],
  );

  if (!visible) return null;

  const containerClasses = mergeClassNames(
    styles.container,
    styles[size],
    className,
  );

  const renderHeader = (): React.ReactNode => {
    if (!header && !closable) return null;

    return (
      <div className={styles.header}>
        {typeof header === "string" ? (
          <h2 className={styles.headerTitle}>{header}</h2>
        ) : (
          header ?? <span />
        )}
        {closable && (
          <IconButton
            icon="close"
            variant="text"
            severity="neutral"
            aria-label="Fechar"
            onClick={onHide}
          />
        )}
      </div>
    );
  };

  const modal = (
    <div
      className={styles.overlay}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={typeof header === "string" ? "modal-title" : undefined}
    >
      <div
        ref={containerRef}
        className={containerClasses}
        onKeyDown={handleKeyDown}
        tabIndex={-1}
      >
        {renderHeader()}
        <div className={styles.body}>{children}</div>
        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

Modal.displayName = "Modal";

export default Modal;
